class VariantPickerCabinetDoor extends HTMLElement {
    static HOLE_PRICE = 72;
    static EDGE_BANDING_PRICE = 384;
    static PROCESSING_FEE = 1200;
    static CUT_TO_SIZE_PRICE = 30;
    static CALCULATOR = "Cabinet Door";

    static SELECTORS = {
        // Form & validation
        form: 'form',

        // Variant data
        variantData: 'script[data-selected-variant]',
        variantInput: 'input[name="id"]',

        // Variant option selects
        optionSelect: '.single-option-selector',
        optionFieldset: '.variant-option',

        // Cabinet compatibility & size
        compatibilitySelect: '#cabinet-compatibility',
        sizeSelect: '#cabinet-size',

        // Cut-to-size inputs
        cutWidth: '#cut-to-size-width',
        cutHeight: '#cut-to-size-height',

        // Hinge holes & hinge options
        hingeHolesSelect: '#hinge-holes',
        hingeRadio: 'input[name="options-hinge"]:checked',
        hingeRadioInput: 'input[name="options-hinge"]',
        hingeOptionItem: '.list-group-item',
        hingeMultiplierRow: '.hinge-holes-multiplier-row',
        hingeMultiplierText: '.hinge-holes-multiplier',

        // Price & quantity
        price: '.variant-price',
        quantity: '#form-quantity',

        // Regions re-rendered on variant change (option fieldsets are handled
        // separately via optionFieldset, matched by fieldset id)
        updateTargets: [
            '.product-price',
            '.btn-action',
            '.product-inventory-status',
            '.product-meta-info',
        ],
    };

    static ATTRS = {
        cutToSize: 'data-cut-to-size',
        hingePicker: 'data-hinge-picker',
    };

    constructor() {
        super();
        this.SELECTORS = VariantPickerCabinetDoor.SELECTORS;
        this.addEventListener('change', this.onVariantChange.bind(this));
    }

    connectedCallback() {
        this.initValidation();
        this.loadReadySizes();
    }

    initValidation() {
        // Prevent double initialization if connectedCallback fires again
        if (this.pristine) return;

        this.form = this.querySelector(this.SELECTORS.form);
        if (!this.form) return;

        // Initialize Pristine validation
        this.pristine = new Pristine(this.form, {
            errorTextClass: 'mt-1 text-sm text-danger font-medium',
            errorTextParent: 'form-group',
        });

        // Use AbortController so listeners can be removed on reinit
        this._submitAbortController = new AbortController();
        const signal = this._submitAbortController.signal;

        // Intercept form submission
        this.form.addEventListener('submit', this.onFormSubmit.bind(this), { signal });
    }

    loadReadySizes() {
        fetch(window.Shopify.customAssets.cabinetDoorSizes)
            .then((response) => response.json())
            .then((data) => {
                this.sizes = data;

                // Populate the size select based on the currently selected compatibility
                this.populateSizeOptions();
            })
            .catch((error) => {
                console.error('VariantPickerCabinetDoor: Failed to load sizes:', error);
                const sizeSelect = this.querySelector(this.SELECTORS.sizeSelect);
                if (sizeSelect) {
                    sizeSelect.innerHTML = '';
                    const placeholder = document.createElement('option');
                    placeholder.value = '';
                    placeholder.textContent = 'Sizes unavailable';
                    placeholder.disabled = true;
                    sizeSelect.appendChild(placeholder);
                }
            });
    }

    async onFormSubmit(e) {
        e.preventDefault();

        if (!this.pristine.validate()) {
            return false;
        }

        const values = this.readFormState();
        if (!values) return false;

        const dimensions = this.resolveDimensions(values);

        this.setAttribute('inert', '');

        try {
            const formData = this.buildVariantFormData(e.target);

            // Line-item properties (Width/Height) must be set before the
            // variant API call so both the API and cart/add.js receive them.
            if (dimensions) {
                formData.set('properties[Height]', `${dimensions.heightMm} mm`);
                formData.set('properties[Width]', `${dimensions.widthMm} mm`);
            }

            const variant = await CartUtils.addVariant(formData);
            formData.set('id', variant.id);

            // Add the door variant to the cart
            await this.addItemToCart(formData);

            // Add the hinge to the cart (if one was selected)
            const hingeId = this.getSelectedHingeId();
            if (hingeId) {
                const formData = new FormData();
                formData.set('id', hingeId);
                formData.set('quantity', values.hingeHoles * values.quantity);
                await this.addItemToCart(formData);
            }

            // Confirm items are in cart & open the mini-cart sidepanel
            const confirmIds = hingeId
                ? [String(variant.id), String(hingeId)]
                : [String(variant.id)];

            await CartUtils.confirmAndOpenCart(confirmIds);
        } catch (error) {
            console.error('VariantPickerCabinetDoor: Failed to add items to cart:', error);
        } finally {
            this.removeAttribute('inert');
        }
    }

    /**
     * Build the payload for the variant API. The API matches options by
     * name (text), not by option ID, so the option value IDs are replaced
     * with the selected option text.
     * @param {HTMLFormElement} form
     * @returns {FormData}
     */
    buildVariantFormData(form) {
        const formData = new FormData(form);
        formData.set('calculator', VariantPickerCabinetDoor.CALCULATOR);

        this.querySelectorAll(this.SELECTORS.optionSelect).forEach((select) => {
            if (!select.name) return;
            formData.set(select.name, this.getSelectedOptionText(select));
        });

        return formData;
    }

    /**
     * @returns {string|null} The selected hinge variant id, or null if none.
     */
    getSelectedHingeId() {
        const hinge = this.querySelector(this.SELECTORS.hingeRadio);
        return (hinge && hinge.value && hinge.value !== '0') ? hinge.value : null;
    }

    /**
     * POST the given FormData to cart/add.js, throwing on HTTP errors.
     * @param {FormData} formData
     */
    async addItemToCart(formData) {
        const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
            method: 'POST',
            body: formData,
        });

        if (!response.ok) {
            throw new Error('cart/add.js: HTTP ' + response.status);
        }
    }

    getSelectedVariant() {
        const variantData = this.querySelector(this.SELECTORS.variantData);
        if (!variantData) {
            console.warn('VariantPickerCabinetDoor: script[data-selected-variant] not found');
            return null;
        }

        try {
            return JSON.parse(variantData.textContent);
        } catch (e) {
            console.error('VariantPickerCabinetDoor: Invalid variant JSON', e);
            return null;
        }
    }

    onVariantChange(event) {
        const target = event.target;

        // If a variant option select changed, re-render the page with new option
        // values. Shopify resolves the matching variant server-side from the
        // selected option values, so this runs even when no variant is known yet.
        // (reloadSection re-applies price internally, so no need to update it here)
        if (target.matches(this.SELECTORS.optionSelect)) {
            return this.reloadSection();
        }

        const variant = this.getSelectedVariant();
        if (!variant) return;

        // If cabinet compatibility or size changed, rebuild the size options
        if (target.matches(this.SELECTORS.compatibilitySelect)) {
            this.populateSizeOptions();
        }

        // Update price for any change that affects the calculation
        // (cabinet-size, cut-to-size, hinge holes, quantity, etc.)
        const values = this.readFormState();
        if (!values) return;

        this.toggleAttribute(VariantPickerCabinetDoor.ATTRS.hingePicker, values.hingeHoles);
        this.updatePriceDisplay(values, variant);
    }

    /**
     * Parse a size string like "715 x 396 mm" into { heightMm, widthMm }.
     * @param {string} sizeStr
     * @returns {{ heightMm: number, widthMm: number } | null}
     */
    parseSizeString(sizeStr) {
        const match = sizeStr.match(/^(\d+)\s*x\s*(\d+)/i);
        if (!match) return null;
        return {
            heightMm: parseInt(match[1], 10),
            widthMm: parseInt(match[2], 10),
        };
    }

    updatePriceDisplay(values, variant) {
        if (!values) return;

        // If variant not passed (e.g. called from reloadSection), read from DOM
        if (!variant) {
            variant = this.getSelectedVariant();
            if (!variant) return;
        }

        const totalCents = this.calculateTotalCents(values, variant, this.resolveDimensions(values));

        const priceEl = this.querySelector(this.SELECTORS.price);
        if (priceEl) {
            priceEl.innerHTML = Shopify.formatMoney(totalCents, window.money_format_v2 || window.money_format);
        }

        // Hinge multiplier labels are a separate DOM concern from the price itself
        this.updateHingeMultipliers(values);
    }

    /**
     * Resolve the door dimensions (mm) from the cut-to-size inputs or the
     * selected standard size, whichever mode is active. Returns null when no
     * usable dimensions can be resolved (e.g. empty cut-to-size fields).
     * @param {object} values - Current form state (see readFormState).
     * @returns {{ heightMm: number, widthMm: number } | null}
     */
    resolveDimensions(values) {
        let heightMm, widthMm;

        if (values.cutToSize) {
            // Custom dimensions from cut-to-size inputs
            heightMm = values.cutHeight;
            widthMm = values.cutWidth;
        } else {
            // Standard size selected from the dropdown
            const parsed = this.parseSizeString(values.size);
            if (parsed) {
                heightMm = parsed.heightMm;
                widthMm = parsed.widthMm;
            }
        }

        return (heightMm && widthMm)
            ? { heightMm, widthMm }
            : null;
    }

    /**
     * Calculate the total door price in cents for the current selection.
     * @param {object} values - Current form state (see readFormState).
     * @param {object} variant - Selected variant (base price per sqm, in cents).
     * @param {{ heightMm: number, widthMm: number } | null} dimensions
     * @returns {number}
     */
    calculateTotalCents(values, variant, dimensions) {
        // Calculate SQM (variant.price is the base price per square metre, in cents)
        let doorPriceCents = 0;
        if (dimensions) {
            const sqm = (dimensions.heightMm / 1000) * (dimensions.widthMm / 1000);
            doorPriceCents = Math.round(variant.price * sqm);
        } else {
            // Fallback: use variant price as-is (e.g. if no size selected yet)
            doorPriceCents = variant.price;
        }

        const processingFeeCents = VariantPickerCabinetDoor.PROCESSING_FEE;
        const holeTotalCents = Math.round(values.hingeHoles * VariantPickerCabinetDoor.HOLE_PRICE);
        const handlePriceCents = (values.hingePrice || 0) * values.hingeHoles;
        const handleCutToSizeCents = values.cutToSize ? VariantPickerCabinetDoor.CUT_TO_SIZE_PRICE : 0;
        const edgeBandingCents = dimensions
            ? Math.round(((2 * (dimensions.heightMm + dimensions.widthMm)) / 1000) * VariantPickerCabinetDoor.EDGE_BANDING_PRICE)
            : 0;

        return (doorPriceCents + holeTotalCents + handlePriceCents + handleCutToSizeCents + edgeBandingCents + processingFeeCents) * values.quantity;
    }

    /**
     * Show/hide the "n x £x.xx" multiplier labels next to each hinge option,
     * based on the number of hinge holes and the hinge unit price.
     * @param {object} values - Current form state (see readFormState).
     */
    updateHingeMultipliers(values) {
        const multiplierRows = this.querySelectorAll(this.SELECTORS.hingeMultiplierRow);
        multiplierRows.forEach(row => {
            const multiplierText = row.querySelector(this.SELECTORS.hingeMultiplierText);
            if (!multiplierText) return;

            // Find the radio input's data-price from the same list-group-item
            const listItem = row.closest(this.SELECTORS.hingeOptionItem);
            const radio = listItem && listItem.querySelector(this.SELECTORS.hingeRadioInput);
            const hingePriceCents = radio ? parseInt(radio.dataset.price) || 0 : 0;

            if (values.hingeHoles > 0 && hingePriceCents > 0) {
                multiplierText.textContent = `${values.hingeHoles * values.quantity} x `;
                row.classList.remove('d-none');
            } else {
                row.classList.add('d-none');
                multiplierText.textContent = '';
            }
        });
    }

    populateSizeOptions() {
        const compatibilitySelect = this.querySelector(this.SELECTORS.compatibilitySelect);
        const sizeSelect = this.querySelector(this.SELECTORS.sizeSelect);
        if (!compatibilitySelect || !sizeSelect || !this.sizes) return;

        const compatibilityValue = parseInt(compatibilitySelect.value);
        const category = this.sizes.find((size) => size.value === compatibilityValue);
        const options = (category && category.options) ? category.options : [];

        sizeSelect.innerHTML = '';

        this.toggleAttribute(VariantPickerCabinetDoor.ATTRS.cutToSize, compatibilityValue === 5);

        options.forEach((size) => {
            const option = document.createElement('option');
            option.value = size;
            option.textContent = size;
            sizeSelect.appendChild(option);
        });

        // Update price after size options are rebuilt (first option auto-selected)
        const variant = this.getSelectedVariant();
        const values = this.readFormState();
        if (variant && values) {
            this.updatePriceDisplay(values, variant);
        }
    }

    readFormState() {
        const sizeSelect = this.querySelector(this.SELECTORS.sizeSelect);
        const hingeHolesSelect = this.querySelector(this.SELECTORS.hingeHolesSelect);
        const hingeRadio = this.querySelector(this.SELECTORS.hingeRadio);
        const cutWidth = this.querySelector(this.SELECTORS.cutWidth);
        const cutHeight = this.querySelector(this.SELECTORS.cutHeight);
        const quantity = this.querySelector(this.SELECTORS.quantity);

        if (!hingeHolesSelect || !cutWidth || !cutHeight) {
            console.warn('VariantPickerCabinetDoor: Required DOM elements not found');
            return null;
        }

        // Parse hinge handle price from data-price (in cents), default to 0 if no selection
        const hingePrice = hingeRadio ? parseInt(hingeRadio.dataset.price) || 0 : 0;

        // Determine if cut-to-size mode is active (set by data-cut-to-size attribute on host)
        const cutToSize = this.hasAttribute(VariantPickerCabinetDoor.ATTRS.cutToSize);

        // Determine the size string from the cabinet-size select
        let size = '';
        if (sizeSelect && sizeSelect.selectedIndex >= 0) {
            size = sizeSelect.options[sizeSelect.selectedIndex].textContent.trim();
        }

        return {
            size,
            cutToSize,
            hingeHoles: parseInt(hingeHolesSelect.value),
            hingePrice,
            cutWidth: parseInt(cutWidth.value),
            cutHeight: parseInt(cutHeight.value),
            quantity: parseInt(quantity.value),
        };
    }

    /**
     * Get the visible text of the selected option in a single-option-selector,
     * with the " - Out of Stock" suffix stripped.
     * @param {HTMLSelectElement} select
     * @returns {string}
     */
    getSelectedOptionText(select) {
        const option = select.options[select.selectedIndex];
        if (!option) return '';
        return this.getOptionText(option);
    }

    /**
     * Get the visible text of an option element, with the " - Out of Stock"
     * suffix stripped (the snippet appends it to unavailable options).
     * @param {HTMLOptionElement} option
     * @returns {string}
     */
    getOptionText(option) {
        if (!option) return '';
        return option.textContent.trim().replace(/\s*-\s*Out of Stock\s*$/i, '');
    }

    /**
     * Collect the currently selected option value ids (in option order) from
     * the .single-option-selector selects.
     * @returns {string[]}
     */
    getSelectedOptionValues() {
        return Array.from(this.querySelectorAll(this.SELECTORS.optionSelect), (select) => select.value);
    }

    async reloadSection() {
        const sectionId = this.dataset.section;
        // inert is set synchronously, locking the form for the whole reload,
        // so a new option change can't start a second reload until this finishes.
        this.setAttribute('inert', '');

        try {
            const selectedValues = this.getSelectedOptionValues();

            const searchParams = new URLSearchParams();
            searchParams.set('option_values', selectedValues.join(','));
            searchParams.set('section_id', sectionId);

            const url = `${window.location.pathname}?${searchParams.toString()}`;

            const response = await fetch(url);
            if (!response.ok) throw new Error('Network response was not ok');

            const responseText = await response.text();
            const html = new DOMParser().parseFromString(responseText, 'text/html');

            this.applySectionUpdate(html);
            this.syncVariantUrl(html);

            // Re-apply price adjustments (e.g. hinge holes) after server DOM update
            this.updatePriceDisplay(this.readFormState());
        } catch (error) {
            console.error('Variant update error:', error);
        } finally {
            this.removeAttribute('inert');
        }
    }

    applySectionUpdate(html) {
        // The fetched HTML contains the whole section; scope queries to the
        // re-rendered <variant-picker-cabinet-door> element when possible.
        const sourceRoot = html.querySelector('variant-picker-cabinet-door') || html;

        // Update every variant option fieldset (matched by id, fallback by index)
        const sourceOptions = Array.from(sourceRoot.querySelectorAll(this.SELECTORS.optionFieldset));
        const targetOptions = Array.from(this.querySelectorAll(this.SELECTORS.optionFieldset));
        sourceOptions.forEach((source, index) => {
            const target = targetOptions.find(t => t.id === source.id) || targetOptions[index];
            if (source && target) {
                target.innerHTML = source.innerHTML;
            }
        });

        // Update external regions re-rendered on variant change.
        // Prefer the component's own elements (e.g. its .product-price) and only
        // fall back to the whole page for truly external regions like .product-meta-info.
        this.SELECTORS.updateTargets.forEach(selector => {
            // Source lives inside the component (e.g. .product-price), but some
            // regions like .product-meta-info are rendered outside it — fall back
            // to the whole fetched section for those.
            const source = sourceRoot.querySelector(selector) || html.querySelector(selector);
            const target = this.querySelector(selector) || document.querySelector(selector);
            if (source && target) {
                target.innerHTML = source.innerHTML;
            }
        });

        // Update the master variant id input so the form submits the correct variant
        const sourceId = sourceRoot.querySelector(this.SELECTORS.variantInput);
        const targetId = this.querySelector(this.SELECTORS.variantInput);
        if (sourceId && targetId) {
            targetId.value = sourceId.value;
        }

        // Update data-selected-variant script with new variant info from server
        const sourceScript = sourceRoot.querySelector(this.SELECTORS.variantData);
        const targetScript = this.querySelector(this.SELECTORS.variantData);
        if (sourceScript && targetScript) {
            targetScript.textContent = sourceScript.textContent;
        }
    }

    syncVariantUrl(html) {
        const variantId = html.querySelector(this.SELECTORS.variantInput)?.value;
        if (variantId) {
            const url = new URL(window.location.href);
            url.searchParams.set('variant', variantId);
            window.history.replaceState({ path: url.href }, '', url.href);
        }
    }
}


// Define the component
if (!customElements.get('variant-picker-cabinet-door')) {
    customElements.define('variant-picker-cabinet-door', VariantPickerCabinetDoor);
}